import { PhoneConfig } from './types';

/**
 * Audio quality tuning for WebRTC calls, in one place.
 *
 * We already get Opus (WebRTC's default, wideband, high quality). These
 * settings squeeze out the rest of what's in OUR control:
 *  - mic processing: echo cancellation, noise suppression, auto-gain;
 *  - a healthy Opus bitrate + in-band FEC via an SDP tweak (DTX stays OFF —
 *    see tuneOpusSdp);
 *  - the provider's TURN relay when configured (stabilizes NAT paths).
 * Everything past 019's gateway (the codec on the PSTN leg) is theirs.
 */

// Constraints passed to getUserMedia for the local mic. Explicit is
// better than `audio: true` — some browsers leave noiseSuppression /
// autoGainControl off by default.
export const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  // Telephony is mono; asking for 1 channel avoids needless width.
  channelCount: 1,
};

// Some SDKs type mediaConstraints.audio as a boolean but forward the value
// straight to getUserMedia, which accepts a full constraints object. `as any`
// lets the rich mic settings through that boundary.
export const mediaConstraints = { audio: AUDIO_CONSTRAINTS, video: false } as any;

// Persisted preferred-mic deviceId. Without this, getUserMedia uses the SYSTEM
// DEFAULT mic — which on a laptop is the built-in mic even when a headset
// (e.g. AirPods) is connected for playback. Field-confirmed: reps sounded fine
// close to the laptop and terrible when they moved away, because the laptop's
// far-field mic was live instead of the headset's.
const MIC_KEY = 'telephony.micDeviceId';

export function getPreferredMicId(): string | null {
  try { return localStorage.getItem(MIC_KEY) || null; } catch { return null; }
}

export function setPreferredMicId(deviceId: string | null): void {
  try {
    if (deviceId) localStorage.setItem(MIC_KEY, deviceId);
    else localStorage.removeItem(MIC_KEY);
  } catch { /* noop */ }
}

/**
 * Build the getUserMedia constraints with the rep's chosen mic. Falls back to
 * the system default when none is chosen.
 */
export function getMediaConstraints(): any {
  const deviceId = getPreferredMicId();
  const audio: MediaTrackConstraints = { ...AUDIO_CONSTRAINTS };
  // Use `exact` — a PLAIN deviceId is only a soft hint the browser can (and
  // does) ignore, keeping the system-default mic. Field report: reps chose
  // AirPods but were still picked up by the laptop mic. `exact` actually
  // forces the chosen device. If it's unplugged, getUserMedia rejects — the
  // caller (getMicStream) then retries WITHOUT the deviceId so the call still
  // connects on the default rather than failing.
  if (deviceId) audio.deviceId = { exact: deviceId };
  return { audio, video: false };
}

/**
 * Acquire a mic MediaStream honouring the chosen device, with a safe fallback:
 * if the exact device is unavailable, retry on the system default so a call
 * never fails just because a headset was unplugged. Used for live mic switching
 * (the engine handles the initial getUserMedia itself).
 */
export async function getMicStream(): Promise<MediaStream> {
  const c = getMediaConstraints();
  try {
    return await navigator.mediaDevices.getUserMedia(c);
  } catch {
    // Drop the exact-device constraint and take whatever's available.
    const fallback = { audio: { ...AUDIO_CONSTRAINTS }, video: false };
    return navigator.mediaDevices.getUserMedia(fallback);
  }
}

/** List the available microphones (label + id). Requires a prior mic grant to
 *  see labels; the softphone has already been granted, so labels are present. */
export async function listMicrophones(): Promise<{ deviceId: string; label: string }[]> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((d) => d.kind === 'audioinput')
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `מיקרופון ${i + 1}` }));
  } catch {
    return [];
  }
}

// ── Speaker (output) selection ──────────────────────────────────────────────
// Which device PLAYS the customer's audio to the rep. Unlike the mic, output
// isn't a getUserMedia constraint — it's applied to the <audio> element via
// setSinkId(). Persisted separately.
const SPK_KEY = 'telephony.speakerDeviceId';

export function getPreferredSpeakerId(): string | null {
  try { return localStorage.getItem(SPK_KEY) || null; } catch { return null; }
}

export function setPreferredSpeakerId(deviceId: string | null): void {
  try {
    if (deviceId) localStorage.setItem(SPK_KEY, deviceId);
    else localStorage.removeItem(SPK_KEY);
  } catch { /* noop */ }
}

/** List available speakers/outputs. setSinkId support is required to switch. */
export async function listSpeakers(): Promise<{ deviceId: string; label: string }[]> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((d) => d.kind === 'audiooutput')
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `רמקול ${i + 1}` }));
  } catch {
    return [];
  }
}

/** Route an <audio> element to the chosen speaker (best-effort). Returns false
 *  when the browser lacks setSinkId (e.g. Firefox/Safari) so the UI can hide
 *  the picker there. A missing/unplugged device just leaves the default. */
export async function applySpeaker(el: HTMLAudioElement | null): Promise<boolean> {
  const anyEl = el as any;
  if (!el || typeof anyEl.setSinkId !== 'function') return false;
  const deviceId = getPreferredSpeakerId();
  try {
    await anyEl.setSinkId(deviceId || 'default');
    return true;
  } catch {
    return false;
  }
}

/** Whether the browser can switch audio output at all. */
export function speakerSelectionSupported(): boolean {
  return typeof (HTMLAudioElement.prototype as any).setSinkId === 'function';
}

// Public STUN servers, always included so the browser can discover its
// NAT-mapped address and negotiate a media path. Without ANY ICE server,
// WebRTC only offers host candidates — audio then works only when the
// browser can reach the media server directly, which fails silently on
// many networks (call connects, but there's no audio). STUN is the free
// baseline; a provider TURN relay (below) is the fallback for the tightest
// NATs where even STUN can't get media through.
const DEFAULT_STUN_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

/**
 * RTCPeerConnection config. Always returns STUN servers; appends the
 * provider TURN relay when one is configured. Returning a config (rather
 * than undefined) guarantees ICE can traverse NAT for the audio path.
 */
export function buildPcConfig(cfg: PhoneConfig | null): RTCConfiguration {
  const iceServers: RTCIceServer[] = [...DEFAULT_STUN_SERVERS];
  // Preferred: the full server-minted list (Cloudflare TURN — STUN + real UDP
  // TURN with short-lived credentials). Appended to the STUN baseline so we
  // keep a fallback if any single entry is unreachable.
  if (cfg?.iceServers?.length) {
    iceServers.push(...cfg.iceServers);
  } else if (cfg?.turnUri) {
    // Fallback: a single static TURN relay.
    iceServers.push({ urls: cfg.turnUri, username: cfg.turnUsername, credential: cfg.turnCredential });
  }
  return {
    iceServers,
    // Try direct/host/STUN paths first; TURN relay is the last resort.
    iceTransportPolicy: 'all',
  };
}

// Target Opus bitrate (bps). 32 kbps mono is transparent for voice and
// well above the ~12–20 kbps browsers sometimes settle on.
const OPUS_BITRATE = 32000;

// Wideband-first codec preference. The gateway (019/BlueBe) picks the first
// payload type in OUR m-line that it also supports. Production stats showed
// calls stuck on PCMU/8000 (G.711 narrowband) with a perfect network — the
// gateway just wasn't offered anything better first. 019 confirmed it supports
// `ulaw, alaw, g723, g722, gsm` (NO Opus), and G.722 is wideband (16 kHz) — a
// real quality jump over G.711. So rank: Opus (if the gateway ever adds it) →
// G.722 → then the narrowband fallbacks the gateway already used. We only
// reorder the payload types that are actually present, so nothing is dropped.
const CODEC_PREFERENCE = [
  /opus\/48000/i,   // best, but 019 doesn't offer it today
  /G722\/8000/i,    // wideband — the win here (019 supports it)
  /PCMA\/8000/i,    // G.711 A-law
  /PCMU\/8000/i,    // G.711 μ-law (what we were stuck on)
  /G723\/8000/i,
  /GSM\/8000/i,
];

/**
 * Reorder the audio m-line so the highest-quality mutually-supported codec is
 * offered FIRST. Maps each preference regex to its rtpmap payload type, then
 * moves the matched payload types to the front of the m-line in that order.
 */
function preferBestCodec(sdp: string): string {
  // Build payload-type → rtpmap-name map from the SDP.
  const rtpmaps = new Map<string, string>(); // pt -> "NAME/rate"
  const re = /a=rtpmap:(\d+)\s+([^\r\n]+)/g;
  let mm: RegExpExecArray | null;
  while ((mm = re.exec(sdp)) !== null) rtpmaps.set(mm[1], mm[2]);

  // Ordered list of payload types we want at the front (present ones only).
  const entries = Array.from(rtpmaps.entries());
  const preferredPts: string[] = [];
  for (const pref of CODEC_PREFERENCE) {
    for (const [pt, name] of entries) {
      if (pref.test(name) && !preferredPts.includes(pt)) preferredPts.push(pt);
    }
  }
  if (preferredPts.length === 0) return sdp;

  return sdp.replace(/^(m=audio [^\r\n]*)$/m, (line) => {
    const parts = line.split(' ');
    const head = parts.slice(0, 3);           // "m=audio", port, proto
    const pts = parts.slice(3);               // payload types, current order
    const front = preferredPts.filter(pt => pts.includes(pt));
    const rest = pts.filter(pt => !front.includes(pt));
    const reordered = [...front, ...rest];
    if (reordered.join(' ') === pts.join(' ')) return line; // already ordered
    return [...head, ...reordered].join(' ');
  });
}

// Narrowband audio codecs to STRIP from the offer when a wideband one is
// present. 019's gateway supports both G.722 (wideband) and ulaw/alaw
// (narrowband) and was choosing between them inconsistently — same rep, same
// browser, G.722 on one call and PCMU on the next — even though we offer G.722
// first. Offer-ordering is only a hint the answerer can ignore. Removing the
// narrowband options entirely leaves the gateway no choice but G.722.
// telephone-event (DTMF) and any non-8000 wideband codec are NEVER stripped.
const NARROWBAND_TO_STRIP = [/PCMU\/8000/i, /PCMA\/8000/i, /G723\/8000/i, /GSM\/8000/i];
const WIDEBAND_PRESENT = [/opus\/48000/i, /G722\/8000/i];

/**
 * When a wideband codec is offered, drop the narrowband ones so the gateway
 * must negotiate wideband. Keeps a safety net: if NO wideband codec is present,
 * nothing is stripped (the call still works on whatever the gateway offers).
 */
function forceWideband(sdp: string): string {
  const rtpmaps = new Map<string, string>();
  const re = /a=rtpmap:(\d+)\s+([^\r\n]+)/g;
  let mm: RegExpExecArray | null;
  while ((mm = re.exec(sdp)) !== null) rtpmaps.set(mm[1], mm[2]);
  const entries = Array.from(rtpmaps.entries());

  // Bail out unless at least one wideband codec is actually present.
  const hasWideband = entries.some(([, name]) => WIDEBAND_PRESENT.some(w => w.test(name)));
  if (!hasWideband) return sdp;

  // Payload types to remove.
  const dropPts = new Set(
    entries.filter(([, name]) => NARROWBAND_TO_STRIP.some(n => n.test(name))).map(([pt]) => pt),
  );
  if (dropPts.size === 0) return sdp;

  // 1. Remove the dropped PTs from the m=audio line's payload list.
  let out = sdp.replace(/^(m=audio [^\r\n]*)$/m, (line) => {
    const parts = line.split(' ');
    const head = parts.slice(0, 3);
    const pts = parts.slice(3).filter(pt => !dropPts.has(pt));
    return [...head, ...pts].join(' ');
  });

  // 2. Remove the a=rtpmap / a=fmtp / a=rtcp-fb lines for the dropped PTs.
  out = out
    .split(/\r?\n/)
    .filter((l) => {
      const m2 = l.match(/^a=(?:rtpmap|fmtp|rtcp-fb):(\d+)\b/);
      return !(m2 && dropPts.has(m2[1]));
    })
    .join('\r\n');
  return out;
}

/**
 * SDP munger: prefer the best mutually-supported codec (Opus→G.722→…), FORCE
 * wideband by dropping narrowband codecs when a wideband one is offered, then
 * tune Opus's bitrate + in-band FEC when Opus is in play (DTX stays off — see
 * below). Applied to both the offer (outbound) and answer SDP.
 */
export function tuneOpusSdp(sdp: string): string {
  try {
    // Prefer wideband first, then remove the narrowband fallbacks so the
    // gateway can't pick PCMU when G.722 is on the table.
    sdp = preferBestCodec(sdp);
    sdp = forceWideband(sdp);
    // Opus-specific tuning below only applies if Opus is present.
    const m = sdp.match(/a=rtpmap:(\d+)\s+opus\/48000/i);
    if (!m) return sdp;
    const pt = m[1];
    const fmtpRe = new RegExp(`a=fmtp:${pt} (.*)`);
    // NOTE: `usedtx=1` is deliberately absent. DTX stops sending packets
    // during silence, which is fine browser-to-browser but breaks across
    // 019's PSTN gateway: the gaps are read as dropped audio, so the far
    // side hears us cut out while we hear them perfectly (one-way audio).
    // `usedtx=0` is explicit so a browser default can't re-enable it.
    // useinbandfec stays — it *helps* against packet loss.
    const extra = `maxaveragebitrate=${OPUS_BITRATE};stereo=0;useinbandfec=1;usedtx=0`;
    if (fmtpRe.test(sdp)) {
      // Merge into the existing fmtp line, not duplicating keys.
      return sdp.replace(fmtpRe, (_full, params) => {
        const kept = String(params)
          .split(';')
          .filter(p => !/^(maxaveragebitrate|stereo|useinbandfec|usedtx)=/.test(p.trim()))
          .join(';');
        return `a=fmtp:${pt} ${[kept, extra].filter(Boolean).join(';')}`;
      });
    }
    // No fmtp line yet — add one right after the rtpmap.
    return sdp.replace(m[0], `${m[0]}\r\na=fmtp:${pt} ${extra}`);
  } catch {
    return sdp; // never let tuning break a call
  }
}

/**
 * Wire the Opus SDP tuning onto an RTCSession. The engine fires
 * 'sdp' with a mutable { type, sdp } we can rewrite before it's sent.
 */
export function applyAudioTuning(session: any) {
  try {
    session.on('sdp', (e: { originator: string; type: string; sdp: string }) => {
      e.sdp = tuneOpusSdp(e.sdp);
    });
  } catch { /* best-effort */ }
}

/**
 * Locally-generated ringback tone for outbound calls. 019 doesn't always
 * send early media (183 with SDP) while the far side rings, so relying on
 * the remote stream alone can leave the caller in silence. This synthesizes
 * the ITU/Israel ringback (425 Hz, 1s on / 3s off) with the Web Audio API,
 * so the caller always hears "ringing" until the call is answered or ends.
 */
export class Ringback {
  private ctx: AudioContext | null = null;
  private timer: number | null = null;

  /** Lazily create the AudioContext (shared across the object's lifetime). */
  private ensureCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return null;
    this.ctx = new Ctx();
    return this.ctx;
  }

  /**
   * Unlock audio from within a real user gesture (the dial-button click).
   * Browsers create an AudioContext 'suspended' unless resume() is called
   * during a gesture; the async 'progress' event that starts the tone is
   * too late to count as a gesture, so we prime here. This is why ringback
   * played for one user and not another — timing of the autoplay unlock.
   */
  prime() {
    try {
      const ctx = this.ensureCtx();
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch { /* best-effort */ }
  }

  start() {
    if (this.timer !== null) return; // already ringing
    try {
      const ctx = this.ensureCtx();
      if (!ctx) return;
      // Resume in case the context is still suspended (belt-and-suspenders
      // with prime()); without this a suspended context plays nothing.
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const beep = () => {
        if (!this.ctx) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 425; // ITU/Israel call-progress tone
        gain.gain.value = 0.12;     // audible but not harsh
        osc.connect(gain).connect(ctx.destination);
        const t = ctx.currentTime;
        osc.start(t);
        osc.stop(t + 1.0);          // 1s on
      };
      beep();
      this.timer = window.setInterval(beep, 4000); // 1s on + 3s off cadence
    } catch { this.stop(); }
  }

  /** Stop the tone but keep the (now-unlocked) context for the next call. */
  stop() {
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
  }

  /** Fully tear down (e.g. on unmount). */
  dispose() {
    this.stop();
    if (this.ctx) { try { this.ctx.close(); } catch { /* noop */ } this.ctx = null; }
  }
}

/**
 * Incoming-call ring — the sound the REP hears when a call arrives, distinct
 * from Ringback (which is what the rep hears while WE dial out). A double
 * burst with a fast two-tone warble, then a pause, so it reads as a phone
 * ringing rather than a call-progress tone. Same autoplay caveat as Ringback:
 * the AudioContext unlocks only after some user gesture on the page.
 */
export class IncomingRing {
  private ctx: AudioContext | null = null;
  private timer: number | null = null;

  private ensureCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return null;
    this.ctx = new Ctx();
    return this.ctx;
  }

  prime() {
    try {
      const ctx = this.ensureCtx();
      if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch { /* best-effort */ }
  }

  start() {
    if (this.timer !== null) return; // already ringing
    try {
      const ctx = this.ensureCtx();
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const burst = (at: number, dur: number) => {
        if (!this.ctx) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        gain.gain.value = 0.14;
        // Warble between two tones every 40ms for the whole burst — the "bell".
        for (let t = 0; t < dur; t += 0.04) osc.frequency.setValueAtTime(t % 0.08 < 0.04 ? 880 : 1040, at + t);
        osc.connect(gain).connect(ctx.destination);
        osc.start(at);
        osc.stop(at + dur);
      };
      const cycle = () => { const t = ctx.currentTime; burst(t, 0.45); burst(t + 0.65, 0.45); };
      cycle();
      this.timer = window.setInterval(cycle, 3000); // ring-ring … pause
    } catch { this.stop(); }
  }

  stop() {
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
  }

  dispose() {
    this.stop();
    if (this.ctx) { try { this.ctx.close(); } catch { /* noop */ } this.ctx = null; }
  }
}
